import { useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Materia, Unidad, Voto } from '@condo/contracts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useVotar } from './api';

interface Props {
  condoId: string;
  asambleaId: string;
  materia: Materia;
  unidad: Unidad;
  votoExistente: Voto | undefined;
}

/**
 * Bloque de votación para una unidad dentro de una materia.
 * Si ya existe voto, muestra modo solo-lectura.
 * Si no, muestra los controles apropiados segun tipoVoto.
 */
export function UnidadVoteBlock({
  condoId,
  asambleaId,
  materia,
  unidad,
  votoExistente,
}: Props) {
  const votar = useVotar(condoId, asambleaId, materia.id);
  const [opcionSiNo, setOpcionSiNo] = useState<string>('');
  const [opcionMultiple, setOpcionMultiple] = useState<string>('');
  const [opcionesSeleccionadas, setOpcionesSeleccionadas] = useState<string[]>([]);

  function handleToggleEleccion(opcion: string) {
    const max = materia.cuposDisponibles ?? 1;
    setOpcionesSeleccionadas((prev) => {
      if (prev.includes(opcion)) return prev.filter((o) => o !== opcion);
      if (prev.length >= max) return prev; // max reached, ignore
      return [...prev, opcion];
    });
  }

  async function handleSubmit() {
    let opcion: string | string[];
    if (materia.tipoVoto === 'si_no') {
      if (!opcionSiNo) {
        toast.error('Debes seleccionar una opcion');
        return;
      }
      opcion = opcionSiNo;
    } else if (materia.tipoVoto === 'opciones_multiples') {
      if (!opcionMultiple) {
        toast.error('Debes seleccionar una opcion');
        return;
      }
      opcion = opcionMultiple;
    } else {
      if (opcionesSeleccionadas.length === 0) {
        toast.error('Debes seleccionar al menos una opcion');
        return;
      }
      opcion = opcionesSeleccionadas;
    }

    try {
      await votar.mutateAsync({ unidadId: unidad.id, opcion });
      toast.success(`Voto registrado para unidad ${unidad.numero}`);
    } catch {
      toast.error('No se pudo registrar el voto. Intenta nuevamente.');
    }
  }

  // Modo solo-lectura — ya voted
  if (votoExistente) {
    const opcionLabel = Array.isArray(votoExistente.opcion)
      ? votoExistente.opcion.join(', ')
      : votoExistente.opcion;

    return (
      <div className="flex items-center justify-between gap-4 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] px-4 py-3">
        <div>
          <p className="text-sm font-semibold font-display">
            Unidad {unidad.numero}
            <span className="ml-2 text-xs text-[var(--color-ink-muted)] font-normal">
              alicuota: {(unidad.alicuota * 100).toFixed(2)}%
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />
          <span className="text-sm text-[var(--color-ink-muted)]">
            Ya votaste:{' '}
            <span className="font-semibold text-[var(--color-ink)]">{opcionLabel}</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold font-display">
          Unidad {unidad.numero}
        </p>
        <Badge variant="neutral" className="text-[0.625rem]">
          {(unidad.alicuota * 100).toFixed(2)}% alicuota
        </Badge>
      </div>

      {/* si_no */}
      {materia.tipoVoto === 'si_no' && (
        <div className="flex flex-wrap gap-2">
          {(['si', 'no', 'abstencion'] as const).map((op) => (
            <button
              key={op}
              type="button"
              onClick={() => setOpcionSiNo(op)}
              className={[
                'rounded-md border px-4 py-2 text-sm font-semibold transition-colors',
                opcionSiNo === op
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                  : 'border-[var(--color-hairline)] bg-[var(--color-paper)] text-[var(--color-ink)] hover:bg-[var(--color-paper-dim)]',
              ].join(' ')}
            >
              {op === 'si' ? 'Si' : op === 'no' ? 'No' : 'Abstencion'}
            </button>
          ))}
        </div>
      )}

      {/* opciones_multiples */}
      {materia.tipoVoto === 'opciones_multiples' && (
        <Select value={opcionMultiple} onValueChange={setOpcionMultiple}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Selecciona una opcion..." />
          </SelectTrigger>
          <SelectContent>
            {(materia.opciones ?? []).map((op) => (
              <SelectItem key={op} value={op}>
                {op}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* eleccion_cargos */}
      {materia.tipoVoto === 'eleccion_cargos' && (
        <div className="space-y-2">
          <p className="text-xs text-[var(--color-ink-muted)]">
            Selecciona hasta {materia.cuposDisponibles ?? 1} candidato(s)
          </p>
          <div className="flex flex-wrap gap-2">
            {(materia.opciones ?? []).map((op) => {
              const selected = opcionesSeleccionadas.includes(op);
              return (
                <button
                  key={op}
                  type="button"
                  onClick={() => handleToggleEleccion(op)}
                  className={[
                    'rounded-md border px-3 py-1.5 text-sm transition-colors',
                    selected
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white font-semibold'
                      : 'border-[var(--color-hairline)] bg-[var(--color-paper)] text-[var(--color-ink)] hover:bg-[var(--color-paper-dim)]',
                  ].join(' ')}
                >
                  {op}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Button
        size="sm"
        className="mt-3"
        onClick={() => void handleSubmit()}
        disabled={votar.isPending}
      >
        {votar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
        Confirmar voto
      </Button>
    </div>
  );
}
